import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
  RemovalMode,
} from "@/dbot/common/commonDomain";

import { DecisionType } from "@/common/decisions";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  initArticleTextModels,
  LocalizedStringFieldModel,
} from "@/common/commonForms";

export type RemoveSignatureFromArticlesOfAssociation = {
  type: DecisionType.removeSignatureFromArticlesOfAssociation;
  paragraph?: number;
  removalMode?: RemovalMode;
};

export class RemoveSignatureFromArticlesOfAssociationForm {
  readonly type = DecisionType.removeSignatureFromArticlesOfAssociation;
  readonly dispatch: DbotEventDispatcher;
  readonly data?: RemoveSignatureFromArticlesOfAssociation;
  paragraphNumber: ArticleSelectorModel;
  paragraphContainsMultipleClauses: BooleanFieldModel;
  paragraphTexts: LocalizedStringFieldModel[];

  constructor(
    dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<RemoveSignatureFromArticlesOfAssociation>,
    data?: RemoveSignatureFromArticlesOfAssociation,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.data = data;
    this.paragraphNumber = new ArticleSelectorModel(context.inputState?.articles, data?.paragraph);
    this.paragraphTexts = initArticleTextModels({
      selectorModel: this.paragraphNumber,
      value: data?.removalMode?.type === "UpdateParagraph" ? data.removalMode.newTexts : undefined,
    });
    this.paragraphContainsMultipleClauses = new BooleanFieldModel({
      value: data?.removalMode?.type === "UpdateParagraph",
    });
  }

  init(): void {
    if (!this.paragraphNumber.selected) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success": {
        if (!this.paragraphNumber.selected) {
          this.setParagraphNumber(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SIGNATURE"),
          );
        }
        break;
      }
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return true;
  }

  get isValidSignatureParagraph(): boolean {
    const value = this.paragraphNumber.selected;
    return this.paragraphNumber.isValid && value !== undefined && value > 0;
  }

  setParagraphNumber(paragraphNumber: number | undefined) {
    this.paragraphNumber.setSelected(paragraphNumber);
    this.paragraphTexts = initArticleTextModels({ selectorModel: this.paragraphNumber });
  }

  validate(): boolean {
    return this.isValid;
  }
}

export function toRemovalMode({
  paragraphContainsMultipleClauses,
  paragraphTexts,
}: {
  paragraphContainsMultipleClauses: BooleanFieldModel;
  paragraphTexts: LocalizedStringFieldModel[];
}): RemovalMode {
  if (paragraphContainsMultipleClauses.value) {
    return {
      type: "UpdateParagraph",
      newTexts: paragraphTexts.map((tm) => ({
        language: tm.locale,
        text: tm.model.inputValue,
      })),
    };
  }
  return { type: "RemoveParagraph" };
}
